import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
  IconButton,
  Fab
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Store } from '@mui/icons-material';
import { apiRequest } from '../api/api';

// Colore della pagina Gestione Negozi
const PAGE_COLOR = '#4CAF50';

interface Negozio {
  neg: string;
  negozio: string;
  coordinatore: string;
  codClifor: string;
  dscConto1?: string;
}

const NegozioList: React.FC = () => {
  const [negozi, setNegozi] = useState<Negozio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editingNegozio, setEditingNegozio] = useState<Negozio | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [formData, setFormData] = useState<Negozio>({
    neg: '',
    negozio: '',
    coordinatore: '',
    codClifor: '',
    dscConto1: ''
  });

  useEffect(() => {
    fetchUserRole();
    fetchNegozi();
  }, []);

  const fetchUserRole = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setIsAdmin(payload.role === 'Admin');
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }
  };

  const fetchNegozi = async () => {
    try {
      setLoading(true);
      const data = await apiRequest<Negozio[]>('/negozi');
      setNegozi(data);
      setError(null);
    } catch (err: any) {
      if (err.status === 403) {
        setError('Non hai i permessi per visualizzare la gestione negozi');
      } else {
        setError('Errore nel caricamento dei negozi');
      }
      console.error('Error fetching negozi:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (negozio?: Negozio) => {
    if (negozio) {
      setEditingNegozio(negozio);
      setFormData(negozio);
    } else {
      setEditingNegozio(null);
      setFormData({
        neg: '',
        negozio: '',
        coordinatore: '',
        codClifor: '',
        dscConto1: ''
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingNegozio(null);
    setFormData({
      neg: '',
      negozio: '',
      coordinatore: '',
      codClifor: '',
      dscConto1: ''
    });
  };

  const handleSave = async () => {
    try {
      if (editingNegozio) {
        await apiRequest(`/negozi/${editingNegozio.neg}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
      } else {
        await apiRequest('/negozi', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
      }
      handleClose();
      fetchNegozi();
    } catch (err) {
      setError('Errore nel salvare il negozio');
      console.error('Error saving negozio:', err);
    }
  };

  const handleDelete = async (neg: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo negozio?')) {
      try {
        await apiRequest(`/negozi/${neg}`, {
          method: 'DELETE',
        });
        fetchNegozi();
      } catch (err) {
        setError('Errore nell\'eliminazione del negozio');
        console.error('Error deleting negozio:', err);
      }
    }
  };

  const handleInputChange = (field: keyof Negozio) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Typography>Caricamento...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <Store fontSize="large" sx={{ mr: 2, color: '#4CAF50' }} />
          <Typography variant="h4" component="h1">
            Gestione Negozi {!isAdmin && '(Solo lettura)'}
          </Typography>
        </Box>
      </Box>
      
      {isAdmin && (
        <Fab
          aria-label="add"
          sx={{ 
            position: 'fixed', 
            bottom: 16, 
            right: 16,
            bgcolor: PAGE_COLOR,
            color: 'white',
            '&:hover': {
              bgcolor: '#388E3C', // Versione più scura di #4CAF50
            }
          }}
          onClick={() => handleOpen()}
        >
          <AddIcon />
        </Fab>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!error && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Codice</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Nome negozio</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Coordinatore</TableCell>
                {isAdmin && <TableCell align="center" sx={{ fontWeight: 'bold' }}>Azioni</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {negozi.map((negozio) => (
                <TableRow key={negozio.neg}>
                  <TableCell>{negozio.neg}</TableCell>
                  <TableCell>{negozio.negozio}</TableCell>
                  <TableCell>{negozio.coordinatore}</TableCell>
                  {isAdmin && (
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleOpen(negozio)}
                        size="small"
                        sx={{
                          color: PAGE_COLOR,
                          '&:hover': {
                            bgcolor: 'rgba(76, 175, 80, 0.1)', // Hover con trasparenza
                          }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(negozio.neg)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingNegozio ? 'Modifica Negozio' : 'Nuovo Negozio'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Codice Negozio (3 caratteri)"
              value={formData.neg}
              onChange={handleInputChange('neg')}
              disabled={!!editingNegozio}
              fullWidth
              required
              inputProps={{ maxLength: 3 }}
            />
            <TextField
              label="Nome Negozio"
              value={formData.negozio}
              onChange={handleInputChange('negozio')}
              fullWidth
              required
              inputProps={{ maxLength: 45 }}
            />
            <TextField
              label="Coordinatore"
              value={formData.coordinatore}
              onChange={handleInputChange('coordinatore')}
              fullWidth
              required
              inputProps={{ maxLength: 45 }}
            />
            <TextField
              label="Codice Clifor"
              value={formData.codClifor}
              onChange={handleInputChange('codClifor')}
              fullWidth
              required
              inputProps={{ maxLength: 7 }}
            />
            <TextField
              label="Descrizione Conto (opzionale)"
              value={formData.dscConto1 || ''}
              onChange={handleInputChange('dscConto1')}
              fullWidth
              inputProps={{ maxLength: 80 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annulla</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{
              bgcolor: PAGE_COLOR,
              color: 'white',
              '&:hover': {
                bgcolor: '#388E3C', // Versione più scura di #4CAF50
              }
            }}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NegozioList;
